import { useState, useRef, useEffect } from "react";
import { artists } from "@/mocks/artistData";

export type SortOption = "latest" | "popular" | "reward" | "deadline";
export type DifficultyFilter = "all" | "Easy" | "Medium" | "Hard";
export type SearchMode = "campaign" | "artist";

interface QuestFiltersProps {
  searchQuery: string;
  onSearchChange: (v: string) => void;
  selectedArtist: string;
  onArtistChange: (v: string) => void;
  selectedDifficulty: DifficultyFilter;
  onDifficultyChange: (v: DifficultyFilter) => void;
  sortBy: SortOption;
  onSortChange: (v: SortOption) => void;
  totalCount: number;
}

const sortOptions: { value: SortOption; label: string }[] = [
  { value: "latest", label: "최신순" },
  { value: "popular", label: "인기순" },
  { value: "reward", label: "보상 높은순" },
  { value: "deadline", label: "마감 임박순" },
];

export default function QuestFilters({
  searchQuery,
  onSearchChange,
  selectedArtist,
  onArtistChange,
  sortBy,
  onSortChange,
  totalCount,
}: Omit<QuestFiltersProps, "selectedDifficulty" | "onDifficultyChange"> & {
  selectedDifficulty?: DifficultyFilter;
  onDifficultyChange?: (v: DifficultyFilter) => void;
}) {
  const [searchMode, setSearchMode] = useState<SearchMode>(() =>
    selectedArtist !== "all" ? "artist" : "campaign"
  );
  const [artistInputValue, setArtistInputValue] = useState(() => {
    if (selectedArtist !== "all") {
      const found = artists.find((a) => a.id === selectedArtist);
      return found ? found.name : "";
    }
    return "";
  });

  // 외부에서 selectedArtist 변경 시 동기화
  useEffect(() => {
    if (selectedArtist !== "all") {
      setSearchMode("artist");
      const found = artists.find((a) => a.id === selectedArtist);
      setArtistInputValue(found ? found.name : "");
    } else {
      setArtistInputValue("");
    }
  }, [selectedArtist]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [modeMenuOpen, setModeMenuOpen] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const modeMenuRef = useRef<HTMLDivElement>(null);
  const artistDropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (modeMenuRef.current && !modeMenuRef.current.contains(e.target as Node)) {
        setModeMenuOpen(false);
      }
      if (artistDropRef.current && !artistDropRef.current.contains(e.target as Node) &&
          wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleModeChange = (mode: SearchMode) => {
    setSearchMode(mode);
    setModeMenuOpen(false);
    onSearchChange("");
    onArtistChange("all");
    setArtistInputValue("");
    setDropdownOpen(false);
  };

  const handleArtistSelect = (artistId: string) => {
    onArtistChange(artistId);
    const found = artists.find((a) => a.id === artistId);
    setArtistInputValue(found ? found.name : "");
    onSearchChange("");
    setDropdownOpen(false);
  };

  const handleArtistInput = (val: string) => {
    setArtistInputValue(val);
    onArtistChange("all");
    setDropdownOpen(true);
  };

  const handleArtistClear = () => {
    setArtistInputValue("");
    onArtistChange("all");
    setDropdownOpen(false);
  };

  const selectedArtistData = artists.find((a) => a.id === selectedArtist);

  const artistDropdownList = artists.filter((a) =>
    artistInputValue.trim() === ""
      ? true
      : a.name.toLowerCase().includes(artistInputValue.trim().toLowerCase())
  );

  const hasClear = searchMode === "campaign" ? !!searchQuery : !!(artistInputValue || selectedArtist !== "all");

  return (
    <div className="flex items-center gap-3">
      {/* Search bar wrapper — no overflow-hidden so dropdowns can escape */}
      <div className="flex items-center flex-1 relative" ref={wrapperRef}>
        <div className="flex items-center flex-1 bg-[#0a0a0f] border border-white/10 rounded-xl focus-within:border-white/25 transition-colors">

          {/* Mode Toggle */}
          <div className="relative flex-shrink-0" ref={modeMenuRef}>
            <button
              onClick={() => setModeMenuOpen((p) => !p)}
              className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold hover:text-white transition-colors cursor-pointer whitespace-nowrap border-r border-white/10 rounded-l-xl bg-white/5"
            >
              <i className={searchMode === "campaign" ? "ri-sword-line text-[#00f5d4]" : "ri-user-star-line text-[#f72585]"} />
              <span className={searchMode === "campaign" ? "text-[#00f5d4]" : "text-[#f72585]"}>
                {searchMode === "campaign" ? "캠페인" : "아티스트"}
              </span>
              <i className={`ri-arrow-down-s-line text-xs text-white/40 transition-transform duration-200 ${modeMenuOpen ? "rotate-180" : ""}`} />
            </button>

            {/* Mode dropdown — outside overflow-hidden */}
            {modeMenuOpen && (
              <div className="absolute top-[calc(100%+6px)] left-0 w-36 bg-[#1a1a24] border border-white/15 rounded-xl overflow-hidden z-[100]">
                <button
                  onClick={() => handleModeChange("campaign")}
                  className={`w-full flex items-center gap-2.5 px-4 py-3 text-sm cursor-pointer transition-colors ${
                    searchMode === "campaign" ? "text-[#00f5d4] bg-[#00f5d4]/10" : "text-white/60 hover:text-white hover:bg-white/5"
                  }`}
                >
                  <i className="ri-sword-line" />
                  캠페인
                </button>
                <button
                  onClick={() => handleModeChange("artist")}
                  className={`w-full flex items-center gap-2.5 px-4 py-3 text-sm cursor-pointer transition-colors ${
                    searchMode === "artist" ? "text-[#f72585] bg-[#f72585]/10" : "text-white/60 hover:text-white hover:bg-white/5"
                  }`}
                >
                  <i className="ri-user-star-line" />
                  아티스트
                </button>
              </div>
            )}
          </div>

          {/* Input area */}
          <div className="relative flex-1">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
              {searchMode === "artist" && selectedArtist !== "all" && selectedArtistData ? (
                <div className="w-4 h-4 rounded-full overflow-hidden">
                  <img src={selectedArtistData.avatar} alt={selectedArtistData.name} className="w-full h-full object-cover" />
                </div>
              ) : (
                <i className="ri-search-line text-white/30 text-sm" />
              )}
            </div>

            {searchMode === "campaign" ? (
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder="캠페인 제목, 설명, 태그로 검색..."
                className="w-full bg-transparent pl-10 pr-10 py-2.5 text-sm text-white placeholder-white/25 focus:outline-none"
              />
            ) : (
              <input
                type="text"
                value={artistInputValue}
                onChange={(e) => handleArtistInput(e.target.value)}
                onFocus={() => setDropdownOpen(true)}
                placeholder="아티스트 이름으로 검색..."
                className="w-full bg-transparent pl-10 pr-10 py-2.5 text-sm text-white placeholder-white/25 focus:outline-none"
              />
            )}

            {hasClear && (
              <button
                onClick={() => searchMode === "campaign" ? onSearchChange("") : handleArtistClear()}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-white/30 hover:text-white/60 cursor-pointer transition-colors"
              >
                <i className="ri-close-line text-base" />
              </button>
            )}
          </div>
        </div>

        {/* Artist dropdown — rendered outside the border box */}
        {searchMode === "artist" && dropdownOpen && (
          <div
            ref={artistDropRef}
            className="absolute top-[calc(100%+6px)] left-0 right-0 bg-[#1a1a24] border border-white/15 rounded-xl overflow-hidden z-[100] max-h-60 overflow-y-auto"
          >
            <button
              onClick={() => { handleArtistSelect("all"); setArtistInputValue(""); }}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm cursor-pointer transition-colors ${
                selectedArtist === "all" ? "text-white bg-white/10" : "text-white/60 hover:text-white hover:bg-white/5"
              }`}
            >
              <div className="w-7 h-7 flex items-center justify-center rounded-full bg-white/10 flex-shrink-0">
                <i className="ri-apps-line text-xs text-white/60" />
              </div>
              <span>전체 아티스트</span>
              {selectedArtist === "all" && <i className="ri-check-line ml-auto text-[#00f5d4]" />}
            </button>
            {artistDropdownList.length > 0 ? (
              artistDropdownList.map((a) => (
                <button
                  key={a.id}
                  onClick={() => handleArtistSelect(a.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-sm cursor-pointer transition-colors ${
                    selectedArtist === a.id ? "text-white bg-white/10" : "text-white/60 hover:text-white hover:bg-white/5"
                  }`}
                >
                  <div className="w-7 h-7 rounded-full overflow-hidden flex-shrink-0">
                    <img src={a.avatar} alt={a.name} className="w-full h-full object-cover" />
                  </div>
                  <span>{a.name}</span>
                  <span className="ml-auto text-xs text-white/30">{a.categoryLabel}</span>
                  {selectedArtist === a.id && <i className="ri-check-line text-[#00f5d4]" />}
                </button>
              ))
            ) : (
              <div className="px-4 py-4 text-sm text-white/30 text-center">검색 결과 없음</div>
            )}
          </div>
        )}
      </div>

      {/* Sort */}
      <div className="relative flex-shrink-0">
        <select
          value={sortBy}
          onChange={(e) => onSortChange(e.target.value as SortOption)}
          className="appearance-none bg-[#0a0a0f] border border-white/10 rounded-xl px-4 py-2.5 pr-8 text-sm text-white/70 focus:outline-none focus:border-white/25 cursor-pointer transition-colors whitespace-nowrap"
        >
          {sortOptions.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
          <i className="ri-arrow-down-s-line text-white/40 text-sm" />
        </div>
      </div>

      {/* Result count */}
      <span className="text-white/35 text-sm whitespace-nowrap flex-shrink-0">
        <span className="text-white font-semibold">{totalCount}</span>개
      </span>
    </div>
  );
}
